import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Video, VideoOff, PhoneOff, Phone } from 'lucide-react';

export default function VideoCall({ contactId, contactName, onEnd }) {
  const { user, token } = useAuth();
  const [inCall, setInCall] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);

  useEffect(() => {
    return () => {
      endCall();
    };
  }, []);

  const getWsUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    return `${wsUrl}/ws/${token}`;
  };

  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      pcRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.onicecandidate = (e) => {
        if (e.candidate && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ice_candidate', target_id: contactId, candidate: e.candidate }));
        }
      };

      pc.ontrack = (e) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
        setRemoteStream(e.streams[0]);
      };

      ws.onopen = async () => {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        ws.send(JSON.stringify({ type: 'video_offer', target_id: contactId, offer }));
      };

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'video_answer' && data.answer) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
        } else if (data.type === 'ice_candidate' && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } else if (data.type === 'video_end') {
          endCall();
        }
      };

      setInCall(true);
    } catch (err) {
    }
  };

  const endCall = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'video_end', target_id: contactId }));
      }
      wsRef.current.close();
      wsRef.current = null;
    }
    setInCall(false);
    setRemoteStream(null);
    if (onEnd) onEnd();
  };

  if (!inCall) {
    return (
      <Button onClick={startCall} size="sm" className="rounded-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="start-video-call">
        <Video size={16} /> Video Call
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center" data-testid="video-call-active">
      <p className="text-white text-sm mb-4">Video call with {contactName}</p>
      <div className="relative w-full max-w-3xl aspect-video bg-slate-900 rounded-2xl overflow-hidden">
        <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
        <video ref={localVideoRef} autoPlay playsInline muted className="absolute bottom-4 right-4 w-32 h-24 rounded-xl object-cover border-2 border-white/30" />
      </div>
      <div className="mt-6 flex gap-4">
        <Button onClick={endCall} variant="destructive" className="rounded-full gap-2 px-6" data-testid="end-video-call">
          <PhoneOff size={16} /> End Call
        </Button>
      </div>
    </div>
  );
}
